import { useFormContext } from 'react-hook-form';
import { AllocationTaxStatusDTO } from '../../../../../../../../api/useAccountsQuery';
import { getDefaultAssetAllocation } from '../../../../../../../../helpers';
import { useAccountFormContext } from '../../../../context/useAccountFormContext';
import { getWeightedAllocation } from '../../utils/getWeightedAllocation';
import { useNonSpecificPositions } from '../NonSpecificPositions/useNonSpecificPositions';
import { useSpecificPositions } from '../SpecificPositions/useSpecificPositions';

interface UseAggregatedPositionsParams {
  assetAllocationTaxStatus: AllocationTaxStatusDTO | null;
}

export const useAggregatedPositions = ({
  assetAllocationTaxStatus,
}: UseAggregatedPositionsParams) => {
  const { watch } = useFormContext();

  const [accountType, annuityIsAnnuitized] = watch([
    'bankAccount.AccountType',
    'bankAccount.AnnuityIsAnnuitized',
  ]);

  const {
    options: { DefaultAssetAllocation },
  } = useAccountFormContext();

  const defaultAssetAllocation = getDefaultAssetAllocation(DefaultAssetAllocation, {
    AccountType: accountType,
    TaxStatus: assetAllocationTaxStatus!,
    AnnuityIsAnnuitized: annuityIsAnnuitized,
  });

  const { data: specificPositionsData } = useSpecificPositions({ assetAllocationTaxStatus });
  const { data: nonSpecificPositionsData } = useNonSpecificPositions({ assetAllocationTaxStatus });

  const allocations = [
    ...(specificPositionsData?.aggregatedPositions || []),
    ...(nonSpecificPositionsData?.aggregatedPositions || []),
  ];

  if (allocations.length === 0) {
    return {
      data: null,
    };
  }

  const weightedAllocation = getWeightedAllocation(allocations);

  const chartValues = defaultAssetAllocation.map(asset => ({
    ...asset,
    y: weightedAllocation ? weightedAllocation[asset.id] * 100 : asset.y * 100,
  }));

  return {
    data: { chartValues },
  };
};
